// src/oura/components/NewScanModal.tsx
//
// The New Scan flow as a 3-step modal (gameplan A1):
//   1 · Domain  — the company domain, one at a time.
//   2 · Details — name + the fields that shape the ~50 customer questions
//                 Oura asks the AI engines (anchors + services are REQUIRED
//                 by the API per #3065 — they are the query-quality lever).
//   3 · Confirm — summary, then POST /assessments (202 → toast).
//
// Product decision (23 Jul): the prescreen is NOT a user-facing step —
// qualification verdicts (INVISIBLE etc.) stay backend-internal. `prescreen` in
// @service/aeo remains for the future silent background qualifier.
//
// Step 2 is prefilled from the real crawl endpoint (POST /api/v1/detect,
// ourteam-backend#3687) via `detectBusiness`. It previously came from a
// client-side guess based on the spelling of the domain, which is why the
// suggested areas and services never matched the actual site.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { XIcon, ScanLineIcon, ArrowRightIcon, Loader2Icon, WandSparklesIcon, RefreshCwIcon, LockIcon, AlertTriangleIcon } from "lucide-react";
import { Button } from "../primitives/Button";
import {
    KnowledgeCountLine,
    KnowledgeDuplicateRow,
    KnowledgeListFailedRow,
    KnowledgeNoteBox,
    KnowledgeProblemRow,
    KnowledgeRow,
    KnowledgeTiles,
    knowledgeCounts,
} from "./KnowledgeEntry";
import {
    deleteKnowledge,
    deleteStagedKnowledge,
    listKnowledge,
    listStagedKnowledge,
    stageKnowledgeFile,
    stageKnowledgeText,
    type AeoKnowledgeSource,
} from "@service/aeo";
import { cn } from "../lib/cn";
import { notBusinessSiteReason } from "../lib/detectReason";
import { useUserProfileContext } from "@context/user_profile";
import {
    detectBusiness,
    startAssessment,
    type NewScanInput,
    type StartResult,
} from "../aeo";

const INPUT =
    "h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500";

/**
 * Reduce whatever staff typed or pasted (a bare domain, a full URL with a path,
 * a www. host) down to the bare hostname — the "company domain" the API and the
 * crawl detector both expect. Returns "" when it can't produce a hostname, so
 * the caller can treat that as invalid.
 */
function normalizeDomain(raw: string): string {
    const value = raw.trim().toLowerCase();
    if (!value) return "";
    try {
        const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(withProtocol);
        const host = url.hostname.replace(/^www\./, "");
        // On a host where the PATH is the site, the path is not decoration to
        // be trimmed — it is the only thing that names which site this is. A
        // free Wix address has no other identity, so keeping it is the
        // difference between scanning the client's site and scanning Wix's own
        // landing page. Query and hash still go: those address a view of a
        // page, never a site.
        if (isPathAddressedHost(host)) {
            const path = url.pathname.replace(/\/+$/, "");
            if (path) return `${host}${path}`;
        }
        return host;
    } catch {
        return "";
    }
}

/** The host half of a normalized domain, which may now carry a path. */
function hostOf(normalized: string): string {
    return normalized.split("/")[0];
}

/**
 * The path a domain box swallowed, when swallowing it changed the meaning.
 *
 * `normalizeDomain` reduces anything to a bare hostname, and it has to: the
 * whole platform keys on one — `aeo_businesses.domain` is unique, and eight
 * more tables key on `business_domain`. The backend enforces the same shape and
 * is stricter, rejecting anything its hostname regex does not match. So the FE
 * cannot send a path even if it wanted to.
 *
 * What it CAN do is stop being quiet about it. Pasting
 * `aseps3.wixsite.com/ipulgaul` and being scanned for `aseps3.wixsite.com`
 * gives back a report about a page the client has never seen, and nothing on
 * screen said that would happen.
 *
 * Returns null when the path carried nothing (`/`, or no path at all).
 */
function droppedPath(raw: string): string | null {
    const value = raw.trim();
    if (!value) return null;
    try {
        const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(withProtocol);
        const path = url.pathname.replace(/\/+$/, "");
        return path && path !== "" ? path : null;
    } catch {
        return null;
    }
}

/**
 * A host where the PATH is the site, not a page on it.
 *
 * `username.wixsite.com/sitename` is the one that matters today, because a free
 * Wix site has no other address — and Wix is now a live connector, so these are
 * exactly the businesses arriving. The bare host serves Wix's own page, so a
 * scan of it measures nothing the client owns.
 *
 * Deliberately a list of one. The same shape exists elsewhere, but a rule
 * written for hosts nobody has sent us is a rule nobody has tested.
 */
function isPathAddressedHost(host: string): boolean {
    return /(^|\.)wixsite\.com$/.test(host);
}

/** "a", "a and b", "a, b and c" — so the prefill banner reads like a sentence. */
function joinList(items: string[]): string {
    if (items.length <= 1) return items[0] ?? "";
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * Normalize category casing so "dental clinic", "Dental clinic" and "DENTAL
 * CLINIC" all collapse to the same "Dental Clinic" — the same category typed
 * differently across businesses must not read as different categories once
 * there's a real business list to filter/group by (#3747). Case-only for now;
 * a real controlled taxonomy (autocomplete against existing businesses'
 * categories) is a natural follow-up once that list is live on the FE.
 */
function toTitleCase(raw: string): string {
    return raw.trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A field of removable chips with a type-to-add input — for anchors/services,
 * where staff add and remove individual values rather than editing one long
 * comma string. Enter or a trailing comma commits the current draft as a chip;
 * Backspace on an empty draft removes the last chip. The container is
 * vertically resizable (drag the bottom-right corner) for a business with many
 * services, so it doesn't stay cramped to a single line.
 */
/** The areas or services a re-run carries over, read-only until "Edit the questions". */
function LockedChips({ value }: { value: string[] }) {
    return (
        <div className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-2">
            <LockIcon aria-hidden className="mr-0.5 h-3.5 w-3.5 text-gray-400" />
            {value.map((v) => (
                <span key={v} className="inline-flex h-6 items-center rounded-full bg-white px-2.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                    {v}
                </span>
            ))}
        </div>
    );
}

function ChipInput({
    value,
    onChange,
    placeholder,
    invalid,
}: {
    value: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
    invalid?: boolean;
}) {
    const [draft, setDraft] = useState("");

    const add = (raw: string) => {
        const v = raw.trim();
        if (v && !value.includes(v)) onChange([...value, v]);
        setDraft("");
    };
    const removeAt = (i: number) => onChange(value.filter((_, idx) => idx !== i));

    return (
        <div
            className={cn(
                "flex min-h-[4.75rem] w-full resize-y overflow-auto rounded-md border border-gray-300 p-2",
                "focus-within:border-blue-500",
                invalid && "border-rose-400",
            )}
        >
            <div className="flex flex-1 flex-wrap content-start gap-1.5">
                {value.map((item, i) => (
                    <span
                        key={item}
                        className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 py-1 pl-2.5 pr-1.5 text-xs font-medium text-blue-700"
                    >
                        {item}
                        <button
                            type="button"
                            onClick={() => removeAt(i)}
                            aria-label={`Remove ${item}`}
                            className="rounded-full p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-600"
                        >
                            <XIcon className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    className="min-w-[7rem] flex-1 border-none bg-transparent py-1 text-sm text-gray-900 outline-none placeholder:text-gray-400"
                    value={draft}
                    onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(",")) add(v.slice(0, -1));
                        else setDraft(v);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add(draft);
                        } else if (e.key === "Backspace" && !draft && value.length > 0) {
                            removeAt(value.length - 1);
                        }
                    }}
                    onBlur={() => add(draft)}
                    placeholder={value.length === 0 ? placeholder : ""}
                />
            </div>
        </div>
    );
}

/**
 * Which fields the crawl actually filled, and which it couldn't. The endpoint
 * never fabricates — a site without structured data legitimately returns an
 * empty category or services — so naming the gaps is more useful than a blanket
 * "complete the rest": staff see exactly what still needs them.
 */
interface DetectSummary {
    /**
     * Fields taken from the LAST SCAN, not from the crawl.
     *
     * A third bucket rather than folding these into `filled`, because the
     * banner names where each value came from and "a quick read of the site"
     * would be a false claim about a value the previous run submitted.
     */
    carried: string[];
    filled: string[];
    missing: string[];
}

/** No last-scan values to preserve — a first scan, or a re-run whose domain
 *  has been edited to a different site. */
const NOTHING_CARRIED = {
    name: "",
    category: "",
    anchors: [] as string[],
    city: "",
    services: [] as string[],
} as const;

/**
 * Category field with suggestions drawn from the categories already in use
 * across the pipeline, so the same business type converges on ONE spelling
 * instead of fragmenting into "Dental Clinic" / "dental clinic" / "Dentist".
 * Free text is still allowed — a genuinely new category just isn't in the list
 * yet, and blocking it would be worse than a near-duplicate.
 *
 * Suggestions come from GET /api/v1/businesses (see Businesses.tsx), i.e. only
 * categories that already exist. A canonical server-side taxonomy would be a
 * BE ask; this is the honest client-side version of it.
 */
function CategoryInput({
    value,
    onChange,
    options,
}: {
    value: string;
    onChange: (next: string) => void;
    options: string[];
}) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Close on outside click — a dropdown that only closes on blur swallows the
    // click that opened another control.
    useEffect(() => {
        if (!open) return;
        const onDocDown = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, [open]);

    const q = value.trim().toLowerCase();
    const matches = options.filter((o) => o.toLowerCase().includes(q)).slice(0, 6);
    const isNew = !!q && !options.some((o) => o.toLowerCase() === q);

    return (
        <div ref={wrapRef} className="relative">
            <input
                className={INPUT}
                placeholder="Dental Clinic"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") setOpen(false);
                }}
                onBlur={() => onChange(toTitleCase(value))}
            />
            {open && (matches.length > 0 || isNew) && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                    {matches.map((o) => (
                        <button
                            key={o}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                onChange(o);
                                setOpen(false);
                            }}
                            className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                            {o}
                        </button>
                    ))}
                    {isNew && (
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                onChange(toTitleCase(value));
                                setOpen(false);
                            }}
                            className={cn(
                                "block w-full px-3 py-1.5 text-left text-sm text-blue-700 hover:bg-blue-50",
                                matches.length > 0 && "border-t border-gray-100",
                            )}
                        >
                            Use new category &ldquo;{toTitleCase(value)}&rdquo;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/** One labelled block in the step-3 summary. */
function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="grid grid-cols-[7rem_1fr] items-start gap-3 py-2.5">
            <span className="pt-0.5 text-xs uppercase tracking-wide text-gray-400">{label}</span>
            <div className="min-w-0 text-sm text-gray-900">{children}</div>
        </div>
    );
}

function Field({
    label,
    required,
    hint,
    action,
    children,
}: {
    label: string;
    required?: boolean;
    hint?: string;
    /** A control that belongs to this field, on the label's own line. */
    action?: ReactNode;
    children: ReactNode;
}) {
    return (
        <div>
            <div className="mb-1.5 flex min-h-[20px] items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-900">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                {action}
            </div>
            {children}
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    );
}

export function NewScanModal({
    open,
    onClose,
    onStarted,
    categoryOptions = [],
    prefillDomain,
    prefillInputs,
    prefillName,
    prefillCategory,
    businessId,
}: {
    open: boolean;
    onClose: () => void;
    onStarted: (result: StartResult) => void;
    /** Categories already in use across the pipeline, for the step-2 suggestions. */
    categoryOptions?: string[];
    /**
     * Open straight into a re-scan of this domain: the field is filled and the
     * site is re-read immediately, so the operator lands on the Details step.
     *
     * Re-running deliberately goes through this modal rather than firing a
     * silent run. Anchors and services are what generate the ~50 questions, the
     * detector gets them wrong often enough to matter (ourteam-backend#3840),
     * and a run costs real money — so they get confirmed, not assumed.
     */
    prefillDomain?: string;
    /**
     * The inputs the PREVIOUS scan of this domain was created with (#3508, BE
     * ourteam-backend#4567). When present they seed Details instead of a fresh
     * detect crawl, so a re-run measures the same thing and the scores stay
     * comparable — detect returned page headings ("Frequently Asked Questions",
     * "12 Sep") in place of a curated service list, and the two runs were then
     * not comparable at all.
     *
     * Absent/null means the run stored nothing, which is deliberately distinct
     * from a run that genuinely had none: only the first justifies detect.
     */
    prefillInputs?: { anchors?: { primary?: string[]; city?: string }; services?: string[] } | null;
    /** Business name and category from the same previous scan, seeded alongside. */
    prefillName?: string;
    prefillCategory?: string;
    /**
     * The business being re-scanned, when there is one.
     *
     * Knowledge is keyed on the DOMAIN, so a re-scan's Step 2 looks at a store
     * that already has rows in it — the price list somebody sent last month is
     * what this scan will read. An empty box there invited uploading it a
     * second time.
     *
     * Absent on a brand-new domain: the business row does not exist yet, so
     * the staged rows for the domain are read and removed by domain instead
     * (ourteam-backend#4994).
     */
    businessId?: string;
}) {
    // The signed-in user's name, used as the default "Requested by" (#3509).
    // Their actual name, not `username` — that is a login handle ("admin"), and
    // a column meant to say who to ask about a run is useless full of handles.
    // Falls back to the handle only when no name is on the profile at all.
    const [, userInfo] = useUserProfileContext();
    const signedInName =
        [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ").trim()
        || userInfo?.username
        || "";

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [domain, setDomain] = useState("");
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [anchors, setAnchors] = useState<string[]>([]);
    const [city, setCity] = useState("");
    const [services, setServices] = useState<string[]>([]);
    /**
     * Knowledge staged for this scan (ourteam-backend#4749).
     *
     * Staged rather than attached, because the business row does not exist yet
     * — it is created by the first assessment — so there is no id to hang a
     * file on at Step 2. `POST /assessments` re-parents these onto the run's
     * business, which is the whole reason the staging route exists.
     */
    const [knowledge, setKnowledge] = useState<AeoKnowledgeSource[]>([]);
    const [knowledgeNote, setKnowledgeNote] = useState<{ seed?: string } | null>(null);
    const [knowledgeDupes, setKnowledgeDupes] = useState<string[]>([]);
    /**
     * Uploads that failed, with the server's own words.
     *
     * Never swallowed: a dropped file produced no row and no message, which
     * reads as the picker not having worked. ourteam-backend#4987 was exactly
     * that — every staged file came back 422 and Step 2 said nothing.
     */
    const [knowledgeErrors, setKnowledgeErrors] = useState<{ title: string; message?: string }[]>([]);
    const [knowledgeLoading, setKnowledgeLoading] = useState(false);
    const [knowledgeBusy, setKnowledgeBusy] = useState(false);
    /**
     * The staged list for a domain with no business could not be read.
     *
     * Kept apart from `knowledgeErrors` because it is not one upload that
     * failed: it is "we cannot see what is already saved". While it is set the
     * scan cannot start, since a scan would use knowledge nobody can see. A
     * retry that succeeds clears it.
     */
    const [stagedListError, setStagedListError] = useState<string | null>(null);
    // Each load takes a number; only the latest one may write, so a slow
    // response for an old domain or an old attempt cannot overwrite a newer one.
    const stagedLoadRef = useRef(0);
    // #3509: metadata only — it changes nothing about the run, it answers "who
    // asked for this" when somebody queries a scan weeks later. Defaulted to the
    // signed-in user so the common case is one less thing to type, but editable:
    // a scan is often run on someone else's behalf.
    const [requestedBy, setRequestedBy] = useState("");
    const [touched, setTouched] = useState(false);
    const [detecting, setDetecting] = useState(false);
    // Starting a run costs real money on the BE, so the confirm button must not
    // be clickable twice while POST /assessments is in flight.
    const [starting, setStarting] = useState(false);
    // Which domain the current suggestions came from — so going Back and
    // Continuing again never clobbers staff edits for the same domain.
    const [prefilledFor, setPrefilledFor] = useState<string | null>(null);
    const [detected, setDetected] = useState<DetectSummary | null>(null);
    /**
     * The backend's own sentence when it refused to prefill because the domain
     * is serving a hosting, parked or placeholder page (ourteam-backend#5906).
     * Without it the form would open blank with no explanation. The fields stay
     * empty and editable; null on every normal read.
     */
    const [notBusinessSite, setNotBusinessSite] = useState<string | null>(null);
    /**
     * "Refill from website" (#3954) — a re-run reaching for a fresh read.
     *
     * A re-run prefills from the LAST scan on purpose (#3508), so the scores
     * stay comparable. That is right until the last scan was thin: Bee Choo
     * Origin has 41 outlets and its previous run carried one anchor area,
     * "Malaysia", so every question it generated was tailored to the wrong
     * place. The only alternative was typing 41 outlets by hand.
     *
     * So the fresh read is OFFERED, never automatic — taking it is a decision
     * to break the trend line, and the note below says so in those words.
     */
    const [refilling, setRefilling] = useState(false);
    const [refill, setRefill] = useState<{ tone: "info" | "warn"; text: string } | null>(null);
    /**
     * A re-run asks the same questions unless staff choose otherwise. Areas and
     * services decide the 50, so editing them makes a new question set, and
     * from then on the Visibility page compares only the questions both sets
     * ask. Locked by default, so a rescan never changes the questions by
     * accident; "Edit the questions" is the deliberate route.
     */
    const [editQuestions, setEditQuestions] = useState(false);
    // Sequence number so only the latest in-flight detection can mutate state —
    // guards against a stale response for an earlier domain clobbering the form.
    const detectRequestRef = useRef(0);

    // Reset on every open so each scan starts clean. Bumping the sequence here
    // also invalidates any detection still in flight from a previous session.
    useEffect(() => {
        detectRequestRef.current += 1;
        if (open) {
            setStep(1);
            setDomain(prefillDomain ?? "");
            // #3508: a re-run starts from what the last scan actually submitted.
            // `reusedInputs` also suppresses the detect crawl below, so the
            // curated values are not overwritten a moment after being set.
            setName(prefillName ?? "");
            setCategory(prefillCategory ?? "");
            setAnchors(prefillInputs?.anchors?.primary ?? []);
            setCity(prefillInputs?.anchors?.city ?? "");
            setServices(prefillInputs?.services ?? []);
            setEditQuestions(false);
            // Knowledge is NOT carried across a re-open: the staged rows belong
            // to the domain they were added under, and silently attaching them
            // to the next scan would be the wrong business's price list.
            setKnowledge([]);
            setKnowledgeNote(null);
            setKnowledgeDupes([]);
            setKnowledgeErrors([]);
            setStagedListError(null);
            setRequestedBy(signedInName);
            setTouched(false);
            setDetecting(false);
            setStarting(false);
            setPrefilledFor(null);
            setDetected(null);
            setNotBusinessSite(null);
            setRefilling(false);
            setRefill(null);
            prefillStartedRef.current = null;
        }
    }, [open, prefillDomain, prefillInputs, prefillName, prefillCategory, signedInName]);

    /**
     * A re-scan lands on Details, not on the domain box whose answer we already
     * have. Two things make this fiddlier than it looks:
     *
     * - the reset effect above sets `domain` in the SAME commit, so this has to
     *   wait for the state to land rather than read the stale empty value;
     * - detection flips `detecting`, which re-renders, so a plain guard on
     *   `prefilledFor` (only set once detect RESOLVES) would fire it twice and
     *   double-charge the read. The ref latches on the first attempt.
     */
    const prefillStartedRef = useRef<string | null>(null);
    // Reusable only when the previous run actually stored something — an empty
    // block means "we never captured this", which is exactly the case detect is
    // still the right answer for.
    const reusedInputs = !!(prefillInputs
        && ((prefillInputs.anchors?.primary?.length ?? 0) > 0 || (prefillInputs.services?.length ?? 0) > 0));

    /**
     * Whatever the last scan did submit, whether or not it is enough to skip
     * the crawl.
     *
     * `reusedInputs` above is deliberately strict: anchors and services are
     * what decide the ~50 questions, so without them a re-run cannot claim to
     * measure the same thing and detect still has to propose them. But a
     * record that stored only city and category is still the better answer for
     * city and category — and the fallback used to throw them away, replacing
     * a curated value with a crawl guess the moment the anchors were missing.
     * These are the values detect fills AROUND rather than over.
     */
    const carriedInputs = useMemo(
        () => ({
            name: prefillName?.trim() ?? "",
            category: prefillCategory?.trim() ?? "",
            anchors: prefillInputs?.anchors?.primary ?? [],
            city: prefillInputs?.anchors?.city?.trim() ?? "",
            services: prefillInputs?.services ?? [],
        }),
        [prefillName, prefillCategory, prefillInputs],
    );

    useEffect(() => {
        if (!open || !prefillDomain) return;
        // Do NOT re-derive when the last scan's inputs are in hand: re-detecting
        // is the bug (#3508), not the fallback.
        if (reusedInputs) {
            prefillStartedRef.current = normalizeDomain(prefillDomain);
            // Claim the domain as already-prefilled, so that pressing Back to
            // Step 1 and Continue again does NOT run detect over the curated
            // values. `continueFromDomain` gates on `prefilledFor`, which is
            // otherwise set only when a detect RESOLVES — and detect never runs
            // on this path, so without this the guard is dead and the re-run
            // silently degrades to the crawl this ticket exists to avoid.
            // Reachable in one click: #3509 put "Requested by" on Step 1, and a
            // re-run opens on Step 2, so Back is the only way to reach it.
            setPrefilledFor(normalizeDomain(prefillDomain));
            setStep(2);
            return;
        }
        const target = normalizeDomain(prefillDomain);
        if (!target || prefillStartedRef.current === target) return;
        if (normalizeDomain(domain) !== target) return;   // wait for the reset effect
        prefillStartedRef.current = target;
        void continueFromDomain();
        // continueFromDomain closes over the whole form; the ref above is what
        // bounds this to one run per opened domain.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, prefillDomain, domain, reusedInputs]);

    const normalizedDomain = useMemo(() => normalizeDomain(domain), [domain]);

    /** The three buckets, readable before any detection has resolved. */
    const carriedLabels = detected?.carried ?? [];
    const filledLabels = detected?.filled ?? [];
    const missingLabels = detected?.missing ?? [];
    /** Something is filled in — from the last scan, the crawl, or both. Only a
     *  completely blank result earns the amber "we read nothing" tone; a
     *  fallback that carried city and category over is not that. */
    const knowsSomething = carriedLabels.length + filledLabels.length > 0;

    const input: NewScanInput = useMemo(
        () => ({
            domain: normalizedDomain,
            business_name: name.trim(),
            category: category.trim(),
            anchors: { primary: anchors, city: city.trim() },
            services,
            requested_by: requestedBy.trim(),
        }),
        [normalizedDomain, name, category, anchors, city, services, requestedBy],
    );

    /**
     * What this domain already has.
     *
     * Read when the modal opens on an existing business. Not a cache: the rows
     * belong to the store, and this is the same list the Knowledge tab shows.
     */
    useEffect(() => {
        if (!open || !businessId) return;
        let live = true;
        setKnowledgeLoading(true);
        void listKnowledge(businessId)
            .then((page) => { if (live) setKnowledge(page.sources); })
            .finally(() => { if (live) setKnowledgeLoading(false); });
        return () => { live = false; };
    }, [open, businessId]);

    /**
     * What this domain already has, when there is no business yet.
     *
     * Read once Step 1 is left (the domain is settled and the Knowledge section
     * is reachable), not on every keystroke of the domain. Keyed on the
     * boolean rather than the step itself so moving on to Step 3 (the summary
     * counts these rows) does not read it again. Cleared first so a domain
     * changed on Step 1 never shows the last one's rows.
     */
    const pastDomainStep = step !== 1;

    /**
     * One read of the staged list. A failure is shown and gates the start; it
     * is never turned into an empty list, because an empty list here means
     * "nothing is staged", which is not what a failed read tells anyone.
     */
    const loadStagedKnowledge = useCallback(async (domain: string) => {
        const attempt = ++stagedLoadRef.current;
        setKnowledgeLoading(true);
        try {
            const page = await listStagedKnowledge(domain);
            if (attempt !== stagedLoadRef.current) return;
            setKnowledge(page.sources);
            setStagedListError(null);
        } catch {
            if (attempt !== stagedLoadRef.current) return;
            setStagedListError(
                "The knowledge already saved for this domain could not be read, so it cannot be shown as empty. Try again before starting the assessment.",
            );
        } finally {
            if (attempt === stagedLoadRef.current) setKnowledgeLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open || businessId || !pastDomainStep || !normalizedDomain) return;
        setKnowledge([]);
        setStagedListError(null);
        void loadStagedKnowledge(normalizedDomain);
        // Any load still in flight belongs to the domain or step we just left.
        return () => {
            stagedLoadRef.current += 1;
            setKnowledgeLoading(false);
        };
    }, [open, businessId, pastDomainStep, normalizedDomain, loadStagedKnowledge]);

    /**
     * The staged twin of the business remove below: DELETE by domain, same
     * "removed means gone" rule. A refusal is said in the server's own words,
     * and the list is re-read so a row the server no longer has (404, already
     * deleted elsewhere) does not stay on screen.
     */
    const removeStagedKnowledge = async (id: string) => {
        if (!normalizedDomain) return;
        const row = knowledge.find((k) => k.id === id);
        const r = await deleteStagedKnowledge(id, normalizedDomain);
        if (r.ok) {
            setKnowledge((all) => all.filter((x) => x.id !== id));
            return;
        }
        setKnowledgeErrors((e) => [...e, { title: row?.title ?? "That source", message: r.message }]);
        void loadStagedKnowledge(normalizedDomain);
    };

    /**
     * Remove means DELETE, not "hide".
     *
     * A staged row is already saved against its domain and `adopt_staged`
     * attaches it to this scan, so dropping it from the list without deleting
     * it would leave the client's wrong file feeding every draft while the
     * screen said it was gone.
     */
    const removeKnowledge = async (id: string) => {
        if (!businessId) {
            await removeStagedKnowledge(id);
            return;
        }
        const row = knowledge.find((k) => k.id === id);
        if (await deleteKnowledge(businessId, id)) {
            setKnowledge((all) => all.filter((x) => x.id !== id));
        } else {
            setKnowledgeErrors((e) => [...e, {
                title: row?.title ?? "That source",
                message: "It could not be removed. Nothing was changed.",
            }]);
        }
    };

    const addKnowledgeFiles = async (files: File[]) => {
        if (!normalizedDomain) return;
        setKnowledgeBusy(true);
        // One request per file: the dedupe rule answers per request, so a batch
        // containing one repeat could not say which. Three good files must
        // still land when the fourth is already here.
        for (const file of files) {
            const r = await stageKnowledgeFile(normalizedDomain, file);
            if (r.ok) setKnowledge((k) => [...r.sources, ...k]);
            else if (r.kind === "duplicate") setKnowledgeDupes((d) => [...d, r.title]);
            else setKnowledgeErrors((e) => [...e, { title: r.title, message: r.message }]);
        }
        setKnowledgeBusy(false);
    };

    const addKnowledgeNote = async (draft: { title: string; text: string; content_as_of: string }) => {
        if (!normalizedDomain) return false;
        const r = await stageKnowledgeText(normalizedDomain, draft);
        if (!r.ok) {
            if (r.kind === "duplicate") setKnowledgeDupes((d) => [...d, r.title]);
            else setKnowledgeErrors((e) => [...e, { title: r.title, message: r.message }]);
            return false;
        }
        setKnowledge((k) => [...r.sources, ...k]);
        return true;
    };

    /**
     * Ctrl/Cmd V anywhere in Step 2 opens the note box already holding what was
     * copied.
     *
     * This is how most of this content actually arrives — pasted out of an
     * email or a WhatsApp thread — so the gesture people already make is the
     * gesture that works, instead of click-tile-then-paste.
     *
     * It stands down whenever a field has focus. Pasting a domain into the
     * domain box, or more text into the note box itself, must behave exactly as
     * it always did; a global paste handler that swallowed those would break
     * every other input on the step to serve one.
     */
    useEffect(() => {
        if (!open || step !== 2) return;
        const onPaste = (e: ClipboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            const tag = el?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
            const text = e.clipboardData?.getData("text")?.trim();
            if (!text) return;
            e.preventDefault();
            setKnowledgeNote({ seed: text });
        };
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
    }, [open, step]);

    const domainValid = normalizedDomain.length > 0;
    const errors = {
        name: !input.business_name ? "Enter the business name" : null,
        anchors: input.anchors.primary.length === 0 ? "At least one anchor area is required" : null,
        services: input.services.length === 0 ? "At least one service is required" : null,
    };
    const detailsValid = !errors.name && !errors.anchors && !errors.services;

    if (!open) return null;

    const start = async () => {
        if (starting) return;
        setStarting(true);
        try {
            // Resolves once the BE has accepted or refused the run; the row in
            // the table keeps updating from the job store afterwards.
            onStarted(await startAssessment(input));
            onClose();
        } finally {
            setStarting(false);
        }
    };

    // Continue from step 1: quick-read the site and pre-fill the Details step
    // so staff confirm instead of type. Re-entering with the SAME domain keeps
    // any manual edits; a new domain re-detects.
    /**
     * Replace the areas and the services with what the site says now.
     *
     * REPLACES, never merges. The point is the outlets page: a site with 41
     * branches returns them in priority order (Klang Valley first, because the
     * question set hands out area questions in list order), and merging that
     * into a stale "Malaysia" would bury the ordering the backend just did.
     *
     * A list that comes back EMPTY leaves its field alone. `detect` swallows
     * its own errors and returns empty fields, so an unreachable site and a
     * site with nothing on it are the same answer here — which is why the note
     * says what did not happen rather than guessing which of the two it was.
     *
     * Name, category and city are left as they are: this button is about the
     * two fields that decide the questions.
     */
    const refillFromWebsite = async () => {
        if (refilling || !normalizedDomain) return;
        const requestId = ++detectRequestRef.current;
        setRefilling(true);
        setRefill(null);
        try {
            const d = await detectBusiness(normalizedDomain);
            if (requestId !== detectRequestRef.current) return;
            const nextAnchors = d.anchors.primary;
            const nextServices = d.services;
            if (nextAnchors.length > 0) setAnchors(nextAnchors);
            if (nextServices.length > 0) setServices(nextServices);
            if (nextAnchors.length === 0 && nextServices.length === 0) {
                // Say why when the backend refused, instead of a generic "nothing usable" (#4241).
                const refusedBecause = notBusinessSiteReason(d);
                setRefill({
                    tone: "warn",
                    text: refusedBecause
                        ? `${refusedBecause} Areas and services are unchanged.`
                        : `Nothing usable came back from ${normalizedDomain}. Areas and services are unchanged.`,
                });
                return;
            }
            const got = [
                nextAnchors.length > 0 ? `${nextAnchors.length} area${nextAnchors.length === 1 ? "" : "s"}` : null,
                nextServices.length > 0 ? `${nextServices.length} service${nextServices.length === 1 ? "" : "s"}` : null,
            ].filter(Boolean).join(", ");
            // The half-empty case is named, because a field that did not move
            // looks identical to one the site happened to agree with.
            const kept = nextAnchors.length === 0 ? " Services only — areas are unchanged."
                : nextServices.length === 0 ? " Areas only — services are unchanged."
                : "";
            setRefill({
                tone: "info",
                text: `Refilled from ${normalizedDomain}: ${got}.${kept} Changing areas or services starts a new trend line.`,
            });
        } finally {
            if (requestId === detectRequestRef.current) setRefilling(false);
        }
    };

    const continueFromDomain = async () => {
        setTouched(true);
        if (!domainValid) return;
        setTouched(false);
        if (prefilledFor === input.domain) {
            setStep(2);
            return;
        }
        const requestId = ++detectRequestRef.current;
        setDetecting(true);
        try {
            const d = await detectBusiness(input.domain);
            // A newer domain (or a modal reopen) has superseded this request —
            // drop the stale result so it can't clobber the current form.
            if (requestId !== detectRequestRef.current) return;
            // Detect fills the GAPS around what the last scan already
            // answered, instead of overwriting it. Only while the domain is
            // still the one those values describe: edit it to another site and
            // they stop being about this scan at all, so the crawl wins.
            const keep = prefillDomain && normalizeDomain(prefillDomain) === input.domain
                ? carriedInputs
                : NOTHING_CARRIED;

            const nextName = keep.name || d.business_name;
            const nextCategory = keep.category || toTitleCase(d.category);
            const nextAnchors = keep.anchors.length > 0 ? keep.anchors : d.anchors.primary;
            const nextCity = keep.city || d.anchors.city;
            const nextServices = keep.services.length > 0 ? keep.services : d.services;

            setName(nextName);
            setCategory(nextCategory);
            setAnchors(nextAnchors);
            setCity(nextCity);
            setServices(nextServices);

            // Report per field rather than on `confidence`: a run can be
            // "high" confidence and still have found no services, and staff
            // need to know WHICH boxes are theirs to fill — and, now that a
            // fallback can mix the two sources, which boxes the crawl never
            // touched.
            const carried: string[] = [];
            const filled: string[] = [];
            const missing: string[] = [];
            ([
                ["business name", !!keep.name, !!nextName],
                ["category", !!keep.category, !!nextCategory],
                ["anchor areas", keep.anchors.length > 0, nextAnchors.length > 0],
                ["city", !!keep.city, !!nextCity],
                ["services", keep.services.length > 0, nextServices.length > 0],
            ] as const).forEach(([label, kept, present]) => {
                if (kept) carried.push(label);
                else if (present) filled.push(label);
                else missing.push(label);
            });
            setDetected({ carried, filled, missing });
            // A hosting or parked page: say why the form is empty (#4241).
            setNotBusinessSite(notBusinessSiteReason(d));

            setPrefilledFor(input.domain);
            setStep(2);
        } finally {
            if (requestId === detectRequestRef.current) setDetecting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
            {/* Step 2 is wider: three fields share a row there, which is cramped
                at the default width. Steps 1 and 3 stay narrow. */}
            <div
                className={cn(
                    "relative w-full rounded-lg bg-white shadow-xl transition-[max-width]",
                    step === 2 ? "max-w-3xl" : "max-w-lg",
                )}
            >
                {/* header */}
                <div className="flex items-center justify-between px-6 pb-1 pt-5">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">New scan</h2>
                        {/* One brief line per step, right under the header. The
                            per-field hints and the closing paragraph that used to
                            sit inside step 2 were removed — five separate pieces of
                            guidance on one form read as noise. */}
                        <p className="mt-0.5 text-xs text-gray-500">
                            {step === 1 && "Step 1 of 3 · One domain at a time. We read the site and prefill the rest."}
                            {step === 2 && "Step 2 of 3 · Areas and services shape the questions we ask the AI engines."}
                            {step === 3 && "Step 3 of 3 · Check the details, then run the assessment."}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            {[1, 2, 3].map((s) => (
                                <span
                                    key={s}
                                    className={cn(
                                        "h-1.5 w-6 rounded-full",
                                        s <= step ? "bg-blue-600" : "bg-gray-200",
                                    )}
                                />
                            ))}
                        </div>
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* body */}
                <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
                    {step === 1 && detecting && (
                        <div className="flex flex-col items-center gap-3 py-10 text-center">
                            <Loader2Icon className="h-6 w-6 animate-spin text-blue-600" />
                            <p className="text-sm font-medium text-gray-900">Reading {input.domain}…</p>
                            <p className="mx-auto max-w-[230px] text-xs text-gray-500">
                                Prefilling the business details so you can just confirm them.
                            </p>
                        </div>
                    )}
                    {step === 1 && !detecting && (
                        <div className="space-y-4">
                            <Field label="Company domain" required>
                                <input
                                    className={cn(INPUT, "font-mono", touched && !domainValid && "border-rose-400")}
                                    placeholder="dentalhome.com.my"
                                    value={domain}
                                    onChange={(e) => setDomain(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") void continueFromDomain();
                                    }}
                                    autoFocus
                                />
                            </Field>
                            {touched && !domainValid && (
                                <p className="text-xs text-rose-600">Enter the company domain</p>
                            )}
                            {/* Said before the scan runs, not discovered in the
                                report.

                                A path-addressed host keeps its path now, so the
                                request carries the whole address. The SERVER
                                still reduces it to the host
                                (`_normalize_domain`, ourteam-backend#4822), so
                                until that lands the scan reads the bare host and
                                an operator has to know it. Delete this branch
                                when #4822 ships — it is the only thing here that
                                will stop being true.

                                An ordinary dropped path is not a warning: a
                                pasted /about means the site, and one grey line
                                naming what will be read is enough. */}
                            {domainValid && isPathAddressedHost(hostOf(normalizedDomain)) && droppedPath(domain) ? (
                                <p className="my-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                                    A free Wix address puts the site on a path, so Oura sends the whole thing —{" "}
                                    <span className="font-mono">{normalizedDomain}</span>. Scans still read{" "}
                                    <span className="font-mono">{hostOf(normalizedDomain)}</span> today, which is Wix's own page
                                    rather than this site: check the report covers the right pages, or use the site's own domain
                                    if it has one.
                                </p>
                            ) : domainValid && droppedPath(domain) ? (
                                <p className="my-0 text-xs text-gray-500">
                                    Scanning <span className="font-mono">{normalizedDomain}</span> — the whole site, not just{" "}
                                    <span className="font-mono">{droppedPath(domain)}</span>.
                                </p>
                            ) : null}
                            {/* #3509. Not required: a scan with no requester is
                                still a valid scan, and blocking one over a label
                                would be worse than the missing label. */}
                            <Field label="Requested by">
                                <input
                                    className={INPUT}
                                    placeholder="Who asked for this scan"
                                    value={requestedBy}
                                    onChange={(e) => setRequestedBy(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") void continueFromDomain();
                                    }}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Defaults to you. Change it when you are running this on
                                    someone else&rsquo;s behalf.
                                </p>
                            </Field>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            {/* #3508: say where these values came from. Claiming "a
                                quick read of the site" for values that came off the
                                LAST SCAN would misrepresent what the operator is
                                about to confirm — and the whole point of reusing
                                them is that they are not a fresh read. */}
                            {notBusinessSite ? (
                                // The domain is a hosting or parked page, so nothing was
                                // prefilled. The backend's own sentence, above a form
                                // that stays empty and editable (#4241).
                                <div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                                    <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                    <span>{notBusinessSite}</span>
                                </div>
                            ) : reusedInputs ? (
                                <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                                    <WandSparklesIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                    <span>
                                        Prefilled from this business&rsquo;s last scan, so the re-run
                                        measures the same thing and the scores stay comparable. Edit
                                        anything below before continuing.
                                    </span>
                                </div>
                            ) : (
                            <div
                                className={cn(
                                    "flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs",
                                    knowsSomething
                                        ? "bg-blue-50 text-blue-800"
                                        : "bg-amber-50 text-amber-900",
                                )}
                            >
                                <WandSparklesIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                <span>
                                    {!knowsSomething ? (
                                        <>
                                            We couldn&rsquo;t read anything usable from{" "}
                                            <span className="font-medium">{prefilledFor}</span> — the
                                            site may block crawlers or have little text. Please fill
                                            these in.
                                        </>
                                    ) : (
                                        <>
                                            {/* Named apart from the crawled ones: a value the
                                                last scan submitted is a different kind of answer
                                                from one a crawl guessed a moment ago, and the
                                                operator is about to confirm both in the same
                                                form. */}
                                            {carriedLabels.length > 0 && (
                                                <>
                                                    Kept {joinList(carriedLabels)} from this
                                                    business&rsquo;s last scan.
                                                </>
                                            )}
                                            {filledLabels.length > 0 && (
                                                <>
                                                    {carriedLabels.length > 0 ? " Prefilled " : "Prefilled "}
                                                    {joinList(filledLabels)} from a quick read of{" "}
                                                    <span className="font-medium">{prefilledFor}</span>.
                                                </>
                                            )}
                                            {missingLabels.length > 0 ? (
                                                <>
                                                    {" "}We couldn&rsquo;t find{" "}
                                                    <span className="font-medium">
                                                        {joinList(missingLabels)}
                                                    </span>{" "}
                                                    on the site — please add{" "}
                                                    {missingLabels.length > 1 ? "them" : "it"}.
                                                </>
                                            ) : (
                                                " Check and adjust anything that looks off."
                                            )}
                                        </>
                                    )}
                                </span>
                            </div>
                            )}
                            {/* Name · Category · City share a row — three short,
                                single-value fields. Anchors and Services each get
                                their own full-width row: they hold a variable number
                                of chips and wrap, so sharing a row with a short input
                                left them cramped and misaligned. */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <Field label="Business name" required>
                                    <input
                                        className={cn(INPUT, touched && errors.name && "border-rose-400")}
                                        placeholder="Dental Home"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        autoFocus
                                    />
                                </Field>
                                <Field label="Category">
                                    <CategoryInput
                                        value={category}
                                        onChange={setCategory}
                                        options={categoryOptions}
                                    />
                                </Field>
                                <Field label="City">
                                    <input
                                        className={INPUT}
                                        placeholder="Kuala Lumpur"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                    />
                                </Field>
                            </div>
                            <Field
                                label="Anchor areas"
                                required
                                // Only on a re-run (#3954). A brand-new scan has
                                // just called /detect to fill this form, so the
                                // button would offer to do what already happened.
                                action={reusedInputs && editQuestions ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={refilling}
                                        onClick={() => void refillFromWebsite()}
                                        // A multi-outlet site opens its store
                                        // locator in a browser, so this is a
                                        // 15-30 second read, not an instant one.
                                        title={`Read ${normalizedDomain || "the site"} again and replace the areas and services below. Takes up to 30 seconds.`}
                                    >
                                        {refilling ? (
                                            <>
                                                <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                                Reading the site…
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCwIcon className="h-3.5 w-3.5" />
                                                Refill from website
                                            </>
                                        )}
                                    </Button>
                                ) : undefined}
                            >
                                {reusedInputs && !editQuestions ? (
                                    <LockedChips value={anchors} />
                                ) : (
                                    <ChipInput
                                        value={anchors}
                                        onChange={setAnchors}
                                        placeholder="Mont Kiara, KLGCC"
                                        invalid={touched && !!errors.anchors}
                                    />
                                )}
                            </Field>
                            <Field label="Services" required>
                                {reusedInputs && !editQuestions ? (
                                    <LockedChips value={services} />
                                ) : (
                                    <ChipInput
                                        value={services}
                                        onChange={setServices}
                                        placeholder="veneers, Invisalign"
                                        invalid={touched && !!errors.services}
                                    />
                                )}
                            </Field>
                            {reusedInputs && (
                                <div
                                    className={cn(
                                        "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3 py-2 text-xs",
                                        editQuestions ? "bg-amber-50 text-amber-900" : "bg-gray-50 text-gray-600",
                                    )}
                                >
                                    <span className="min-w-0 flex-1">
                                        {editQuestions
                                            ? "Changing areas or services makes a new question set. The Visibility page marks the change and compares only the questions both sets ask."
                                            : "This rescan asks the same questions as the last one, so every change on the Visibility page is like for like."}
                                    </span>
                                    {!editQuestions && (
                                        <Button variant="outline" size="sm" onClick={() => setEditQuestions(true)}>
                                            Edit the questions
                                        </Button>
                                    )}
                                </div>
                            )}
                            {refill && (
                                <p
                                    className={cn(
                                        "m-0 rounded-lg px-3 py-2 text-xs",
                                        refill.tone === "warn" ? "bg-amber-50 text-amber-900" : "bg-blue-50 text-blue-800",
                                    )}
                                >
                                    {refill.text}
                                </p>
                            )}
                            {touched && !detailsValid && (
                                <p className="text-xs text-rose-600">
                                    {errors.name ?? errors.anchors ?? errors.services}
                                </p>
                            )}

                            {/* Knowledge — a SECTION on this step, never a fourth
                                step. A fourth step reads as required, and every
                                operator would dismiss it on every scan; here it
                                is offered where the other details are given and
                                costs nothing to skip. */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900">Knowledge</span>
                                    <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-500">
                                        Optional
                                    </span>
                                </div>
                                <p className="my-0 mt-1 text-xs leading-relaxed text-gray-500">
                                    Menus, price lists, brochures, anything the site does not publish. Files and pasted
                                    text in any mix. We read them and use them for the whole engagement.
                                </p>

                                <div className="mt-3">
                                    <KnowledgeTiles
                                        onFiles={(files) => void addKnowledgeFiles(files)}
                                        onPaste={() => setKnowledgeNote({})}
                                        disabled={!domainValid || knowledgeBusy}
                                    />
                                </div>

                                {stagedListError && (
                                    <div className="mt-2">
                                        <KnowledgeListFailedRow
                                            message={stagedListError}
                                            retrying={knowledgeLoading}
                                            onRetry={() => void loadStagedKnowledge(normalizedDomain)}
                                        />
                                    </div>
                                )}

                                {knowledgeErrors.map((e, i) => (
                                    <div key={`${e.title}-err-${i}`} className="mt-2">
                                        <KnowledgeProblemRow
                                            title={e.title}
                                            message={e.message}
                                            onDismiss={() => setKnowledgeErrors((all) => all.filter((_, j) => j !== i))}
                                        />
                                    </div>
                                ))}

                                {knowledgeDupes.map((title, i) => (
                                    <div key={`${title}-${i}`} className="mt-2">
                                        <KnowledgeDuplicateRow
                                            title={title}
                                            onDismiss={() => setKnowledgeDupes((d) => d.filter((_, j) => j !== i))}
                                        />
                                    </div>
                                ))}

                                {knowledgeNote && (
                                    <KnowledgeNoteBox
                                        seedText={knowledgeNote.seed}
                                        nextIndex={knowledgeCounts(knowledge).notes + 1}
                                        onAdd={addKnowledgeNote}
                                        onDone={() => setKnowledgeNote(null)}
                                    />
                                )}

                                {knowledge.length > 0 && (
                                    <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
                                        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2">
                                            <span className="text-xs font-medium text-gray-700">
                                                Knowledge sources{knowledgeLoading ? " · reading the store…" : ""}
                                            </span>
                                            <KnowledgeCountLine sources={knowledge} />
                                        </div>
                                        {knowledge.map((k) => (
                                            <KnowledgeRow
                                                key={k.id}
                                                source={k}
                                                onDelete={(id) => void removeKnowledge(id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white px-4">
                            <SummaryRow label="Business">
                                <span className="font-medium">{input.business_name}</span>
                            </SummaryRow>
                            <SummaryRow label="Domain">
                                <span className="break-all font-mono text-xs">{input.domain}</span>
                            </SummaryRow>
                            {input.requested_by && (
                                <SummaryRow label="Requested by">{input.requested_by}</SummaryRow>
                            )}
                            {input.category && (
                                <SummaryRow label="Category">{input.category}</SummaryRow>
                            )}
                            {input.anchors.city && (
                                <SummaryRow label="City">{input.anchors.city}</SummaryRow>
                            )}
                            {/* Chips, not a "·"-joined string — a business covering a
                                dozen areas used to run off the edge with ragged
                                spacing. These wrap cleanly at any count. */}
                            <SummaryRow label={`Areas (${input.anchors.primary.length})`}>
                                <div className="flex flex-wrap gap-1.5">
                                    {input.anchors.primary.map((a) => (
                                        <span
                                            key={a}
                                            className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                                        >
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </SummaryRow>
                            <SummaryRow label={`Services (${input.services.length})`}>
                                <div className="flex flex-wrap gap-1.5">
                                    {input.services.map((s) => (
                                        <span
                                            key={s}
                                            className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </SummaryRow>
                            {/* Named on the summary even when empty. What a run
                                was told is part of what it is, and "None added"
                                is a fact worth being able to read back later. */}
                            <SummaryRow label={`Knowledge (${knowledge.length})`}>
                                {knowledge.length === 0 ? (
                                    <span className="text-gray-400">None added</span>
                                ) : (
                                    <div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {knowledge.map((k) => (
                                                <span
                                                    key={k.id}
                                                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700"
                                                >
                                                    {k.title}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="my-0 mt-1.5 text-xs leading-relaxed text-gray-500">
                                            {knowledgeCounts(knowledge).files} uploaded, {knowledgeCounts(knowledge).notes} pasted.
                                            Recorded against this run, so we can always say what it was told.
                                        </p>
                                    </div>
                                )}
                            </SummaryRow>
                        </div>
                    )}
                </div>

                {/* Why Start is off, said where Start is. The row is the same one
                    Step 2 shows; the retry there and here are the same action. */}
                {step === 3 && stagedListError && (
                    <div className="px-6 pb-2">
                        <KnowledgeListFailedRow
                            message={stagedListError}
                            retrying={knowledgeLoading}
                            onRetry={() => void loadStagedKnowledge(normalizedDomain)}
                        />
                    </div>
                )}

                {/* footer */}
                <div className="flex items-center justify-between px-6 pb-5 pt-1">
                    <Button
                        variant="outline"
                        disabled={starting}
                        onClick={() => (step === 1 ? onClose() : setStep((s) => (s === 3 ? 2 : 1)))}
                    >
                        {step === 1 ? "Cancel" : "Back"}
                    </Button>

                    {step === 1 && (
                        <Button disabled={detecting} onClick={() => void continueFromDomain()}>
                            {detecting ? (
                                <>
                                    <Loader2Icon className="h-4 w-4 animate-spin" />
                                    Reading site…
                                </>
                            ) : (
                                <>
                                    Continue
                                    <ArrowRightIcon className="h-4 w-4" />
                                </>
                            )}
                        </Button>
                    )}
                    {step === 2 && (
                        <Button
                            // Not while the site is being read: the answer is
                            // about to replace two of the fields being confirmed.
                            disabled={refilling}
                            onClick={() => {
                                setTouched(true);
                                if (detailsValid) setStep(3);
                            }}
                        >
                            Continue
                            <ArrowRightIcon className="h-4 w-4" />
                        </Button>
                    )}
                    {step === 3 && (
                        <Button
                            // Not while the domain's staged knowledge is still being read,
                            // and not when it could not be read: the scan would use rows
                            // the operator cannot see. Lifts once a read succeeds. The
                            // business branch has no staged read, so it is unaffected.
                            disabled={starting || (!businessId && (knowledgeLoading || !!stagedListError))}
                            onClick={() => void start()}
                        >
                            {starting ? (
                                <>
                                    <Loader2Icon className="h-4 w-4 animate-spin" />
                                    Starting…
                                </>
                            ) : (
                                <>
                                    <ScanLineIcon className="h-4 w-4" />
                                    Start assessment
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
